import type { RagResultToolDef } from '@kanbu/schema/contracts';
import {
  ContentType,
  SourcesDisplayMode,
  SourcesFormat,
} from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { ChevronRight, File, FileSymlink, Globe } from 'lucide-react';
import { memo, useMemo, useState } from 'react';

import { SourceChip } from './source-chip';
import {
  filterDisplaySources,
  formatPageLabel,
  getHref,
  getSourceDisplayLabel,
  sourceReactKey,
  type RagSource,
} from './source-utils';
import { cn } from '../../lib/cn';

const inlineContainerClassName = 'mt-2 flex flex-wrap gap-1.5';

export interface SourcesBlockProps {
  sources: RagResultToolDef['sources'];
  sourcesFormat?: SourcesFormat;
  sourcesDisplayMode?: SourcesDisplayMode;
  maxVisible?: number;
}

const MAX_VISIBLE_SOURCES = 3;

function StructuredSourceRow({
  source,
  format,
}: {
  source: RagSource;
  format: SourcesFormat;
}) {
  const href = source.url
    ? getHref(source.url, source.page, source.mimeType)
    : undefined;
  const label = getSourceDisplayLabel(source);
  const pageLabel = formatPageLabel(source.page, source.type);

  return (
    <li className='grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1 text-xs'>
      {href ? (
        <a
          className='flex min-w-0 items-center hover:underline'
          href={href}
          target='_blank'
          rel='noreferrer'
          title={[label, source.header, source.article, pageLabel, source.note]
            .filter(Boolean)
            .join(' | ')}
        >
          <SourceTypeIcon type={source.type} />
          <span className='min-w-0 truncate'>
            {format === SourcesFormat.Simple ? (
              label
            ) : (
              <>
                {label}
                {source.header && (
                  <>
                    {' '}
                    | <strong>{source.header}</strong>
                  </>
                )}
                {source.article && ` | ${source.article}`}
              </>
            )}
            {pageLabel && (
              <>
                {' '}
                · <Trans context='page-shortcut'>p</Trans>. {pageLabel}
              </>
            )}
          </span>
        </a>
      ) : (
        <span
          className='flex min-w-0 items-center'
          title={[label, source.header, source.article, pageLabel, source.note]
            .filter(Boolean)
            .join(' | ')}
        >
          <SourceTypeIcon type={source.type} />
          <span className='min-w-0 truncate'>
            {label}
            {pageLabel && (
              <>
                {' '}
                · <Trans context='page-shortcut'>p</Trans>. {pageLabel}
              </>
            )}
          </span>
        </span>
      )}
      {href ? <ChevronRight className='size-3.5 shrink-0' /> : null}
    </li>
  );
}

function SourceTypeIcon({ type }: { type: ContentType }) {
  if (type === ContentType.Document) {
    return <File className='mr-1.5 size-3.5 shrink-0' />;
  }

  if (type === ContentType.Website) {
    return <Globe className='mr-1.5 size-3.5 shrink-0' />;
  }

  if (type === ContentType.ExternalDocument) {
    return <FileSymlink className='mr-1.5 size-3.5 shrink-0' />;
  }

  return null;
}

export const SourcesBlock = memo(
  ({
    sources,
    sourcesFormat = SourcesFormat.Simple,
    sourcesDisplayMode = SourcesDisplayMode.Pills,
    maxVisible = MAX_VISIBLE_SOURCES,
  }: SourcesBlockProps) => {
    const filteredSources = useMemo(
      () => filterDisplaySources(sources),
      [sources],
    );
    const [isExpanded, setIsExpanded] = useState(false);

    if (filteredSources.length === 0) {
      return null;
    }

    const hiddenCount = Math.max(0, filteredSources.length - maxVisible);
    const visibleSources = isExpanded
      ? filteredSources
      : filteredSources.slice(0, maxVisible);

    if (sourcesDisplayMode === SourcesDisplayMode.Pills) {
      return (
        <div className={inlineContainerClassName}>
          {visibleSources.map((source, index) => (
            <SourceChip key={sourceReactKey(source, index)} source={source} />
          ))}
          {!isExpanded && hiddenCount > 0 && (
            <button
              type='button'
              className={cn(
                'inline-block cursor-pointer rounded-full bg-chat-surface px-2 py-1',
                'text-[0.65rem] font-medium leading-none',
              )}
              onClick={() => setIsExpanded(true)}
            >
              +{hiddenCount}
            </button>
          )}
          {isExpanded && hiddenCount > 0 && (
            <button
              type='button'
              className={cn(
                'inline-block cursor-pointer rounded-full bg-chat-surface px-2 py-1',
                'text-[0.65rem] font-medium leading-none',
              )}
              onClick={() => setIsExpanded(false)}
            >
              <Trans>Show less</Trans>
            </button>
          )}
        </div>
      );
    }

    return (
      <>
        <h4 className='text-xs font-semibold mb-2'>
          <Trans>Sources</Trans>
        </h4>
        <ul className='flex flex-col gap-1'>
          {visibleSources.map((source, index) => (
            <StructuredSourceRow
              key={sourceReactKey(source, index)}
              source={source}
              format={sourcesFormat}
            />
          ))}
        </ul>
        {!isExpanded && hiddenCount > 0 && (
          <button
            type='button'
            className='mt-2 text-xs underline'
            onClick={() => setIsExpanded(true)}
          >
            +{hiddenCount} <Trans>more sources</Trans>
          </button>
        )}
        {isExpanded && hiddenCount > 0 && (
          <button
            type='button'
            className='mt-2 text-xs underline'
            onClick={() => setIsExpanded(false)}
          >
            <Trans>Show fewer sources</Trans>
          </button>
        )}
      </>
    );
  },
);
